import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Mail, Lock, ArrowRight, ShieldCheck, AlertCircle } from 'lucide-react';
import { API_BASE_URL } from '../services/api';

const ResetPasswordPage = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [status, setStatus] = useState({ type: '', message: '' });
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (password !== confirmPassword) {
            return setStatus({ type: 'error', message: 'Les mots de passe ne correspondent pas.' });
        }
        if (password.length < 6) {
            return setStatus({ type: 'error', message: 'Le mot de passe doit contenir au moins 6 caractères.' });
        }

        setLoading(true);
        setStatus({ type: '', message: '' });

        try {
            const res = await axios.post(`${API_BASE_URL}/auth/reset-password`, {
                token,
                newPassword: password
            });

            if (res.data.success) {
                setStatus({ type: 'success', message: 'Mot de passe réinitialisé avec succès !' });
                setTimeout(() => navigate('/login'), 2500);
            }
        } catch (error) {
            setStatus({ type: 'error', message: error.response?.data?.message || 'Erreur lors de la réinitialisation.' });
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
                <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
                    <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
                    <h2 className="text-2xl font-bold text-gray-900">Lien invalide</h2>
                    <p className="mt-2 text-gray-600">Aucun jeton de sécurité fourni dans l'URL.</p>
                    <button onClick={() => navigate('/login')} className="mt-6 text-primary hover:text-primary-dark font-medium">
                        Retour à la connexion
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <div className="flex justify-center">
                    <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center shadow-lg transform rotate-3">
                        <ShieldCheck className="w-10 h-10 text-white transform -rotate-3" />
                    </div>
                </div>
                <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 tracking-tight">
                    Nouveau mot de passe
                </h2>
                <p className="mt-2 text-center text-sm text-gray-600">
                    Veuillez choisir un nouveau mot de passe sécurisé.
                </p>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white py-8 px-4 shadow-xl sm:rounded-2xl sm:px-10 border border-gray-100 relative overflow-hidden">
                    <form className="space-y-6" onSubmit={handleSubmit}>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 leading-relaxed">
                                Nouveau mot de passe
                            </label>
                            <div className="mt-2 relative rounded-xl shadow-sm group">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <Lock className="h-5 w-5 text-gray-400 group-focus-within:text-primary transition-colors" />
                                </div>
                                <input
                                    type="password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="block w-full pl-11 pr-3 py-3 border border-gray-200 rounded-xl leading-5 bg-gray-50/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 text-sm placeholder-gray-400"
                                    placeholder="••••••••"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 leading-relaxed">
                                Confirmer le mot de passe
                            </label>
                            <div className="mt-2 relative rounded-xl shadow-sm group">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <Lock className="h-5 w-5 text-gray-400 group-focus-within:text-primary transition-colors" />
                                </div>
                                <input
                                    type="password"
                                    required
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="block w-full pl-11 pr-3 py-3 border border-gray-200 rounded-xl leading-5 bg-gray-50/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 text-sm placeholder-gray-400"
                                    placeholder="••••••••"
                                />
                            </div>
                        </div>

                        {status.message && (
                            <div className={`p-4 rounded-xl text-sm flex items-start ${
                                status.type === 'error' ? 'bg-red-50 text-red-800 border border-red-100' : 'bg-green-50 text-green-800 border border-green-100'
                            }`}>
                                <AlertCircle className={`w-5 h-5 mr-3 flex-shrink-0 ${
                                    status.type === 'error' ? 'text-red-500' : 'text-green-500'
                                }`} />
                                <div>{status.message}</div>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading || status.type === 'success'}
                            className="w-full flex justify-center py-3.5 px-4 border border-transparent rounded-xl shadow-sm text-sm font-semibold text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed group"
                        >
                            {loading ? (
                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : (
                                "Réinitialiser"
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordPage;
